'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'

import { api } from '@/features/veluvit/services/api'
import { useSessionStore } from '@/features/veluvit/stores/session.store'
import { useRenewStore } from '@/features/studio-renew/store'
import {
  ensureStudioCollections,
  resetStudioSync,
} from '@/features/studio-renew/sync'
import { clearLegacyStudioStorage } from '@/features/studio-renew/lib/legacy-storage'
import type { StudioInitialSnapshot } from '@/features/studio-renew/services/studio-read.server'

type Me = { id?: string; email?: string | null; guest?: boolean; name?: string }
type RenewAuth =
  | { state: 'loading' }
  | { state: 'disabled' }
  | { state: 'guest' }
  | { state: 'member'; name: string }
  | { state: 'error'; message: string }

/**
 * 새 스튜디오는 로그인한 회원만 — 게스트·비로그인은 로그인으로 보낸다(기획 결정 10). 연결 코드·관리자 연결은 없다.
 * bootstrap으로 회원 여부를 확인한다. 편집 경로는 필요한 자료를 직접 읽고, 목록·신규 생성 경로는 작품 목록으로 동기화를 준비한다.
 */
export function useRenewAuth(
  initial?: StudioInitialSnapshot['auth']
): RenewAuth {
  const editing =
    /^\/studio-renew\/(work|character|world|status|command)\//.test(
      usePathname()
    )
  const [auth, setAuth] = useState<{
    identity: number
    ready: boolean
    result: RenewAuth
  } | null>(null)
  const identity = useSessionStore((state) => state.identity)
  const [initialIdentity] = useState(() => identity)
  const ready = useSessionStore((state) => state.ready)
  const sessionError = useSessionStore((state) => state.error)
  const user = useSessionStore((state) => state.data?.user)
  const creatorEnabled = useSessionStore(
    (state) => state.data?.config.creator?.enabled
  )
  useEffect(() => {
    clearLegacyStudioStorage()
  }, [])
  useEffect(() => {
    let alive = true
    const current = () =>
      alive && useSessionStore.getState().identity === identity
    // 꺼진 상태는 아래에서 렌더 값으로 바로 돌려준다.
    if (creatorEnabled === false) return
    const open = async (me: Me | null | undefined) => {
      if (!current()) return
      if (!me || me.guest || !me.id) {
        useRenewStore.getState().setOwner(null)
        resetStudioSync()
        setAuth({ identity, ready, result: { state: 'guest' } })
        return
      }
      const owner = me.id
      useRenewStore.getState().setOwner(owner)
      if (!initial && !editing) await ensureStudioCollections(['chat'])
      if (current())
        setAuth({
          identity,
          ready,
          result: { state: 'member', name: me.name || me.email || '작가' },
        })
    }
    // bootstrap 응답이 이미 확인한 회원 정보다. 초기 요청이 실패한 경우에만 /user/로 확인한다.
    if (ready) {
      void open(user).catch((error: unknown) => {
        if (current())
          setAuth({
            identity,
            ready,
            result: {
              state: 'error',
              message:
                error instanceof Error
                  ? error.message
                  : '연결을 확인해 주세요.',
            },
          })
      })
      return () => {
        alive = false
      }
    }
    if (!sessionError)
      return () => {
        alive = false
      }
    api<Me>('/user/')
      .then(async (me) => {
        await open(me)
      })
      .catch((error: unknown) => {
        if (!current()) return
        // 비로그인은 서버가 401/403 으로 답한다 — 오류가 아니라 로그인 안내
        const status = (error as { status?: number })?.status
        if (status === 401 || status === 403) {
          useRenewStore.getState().setOwner(null)
          resetStudioSync()
          setAuth({ identity, ready, result: { state: 'guest' } })
        } else
          setAuth({
            identity,
            ready,
            result: {
              state: 'error',
              message:
                error instanceof Error
                  ? error.message
                  : '연결을 확인해 주세요.',
            },
          })
      })
    return () => {
      alive = false
    }
  }, [identity, ready, sessionError, user, editing, creatorEnabled, initial])
  if (creatorEnabled === false) return { state: 'disabled' }
  if (initial?.state === 'disabled') return { state: 'disabled' }
  if (initial && !ready && !sessionError && identity === initialIdentity) {
    if (initial.state === 'error') return { state: 'loading' }
    return initial.state === 'member'
      ? { state: 'member', name: initial.name }
      : initial
  }
  if (ready && (!user || user.guest)) return { state: 'guest' }
  if (ready && initial?.state === 'member' && user?.id === initial.owner)
    return { state: 'member', name: user.name || initial.name }
  return auth?.identity === identity && auth.ready === ready
    ? auth.result
    : { state: 'loading' }
}
